import { useSessionContext } from '@/contexts/session/context';
import { promiseTimeout, type RetryOptions } from '@/scripts/utils/queue';
import { genId } from '@/scripts/utils';
import { useRefs } from '@/scripts/hooks';
import { chatApis } from '@/configs/apis/chat';
import { ActiveCallResCode } from '@/configs/apis/chat/media';
import { membersApis } from '@/configs/apis/groups/members';
import type { User } from '@/configs/apis/user';
import { useAudio } from '@/scripts/hooks/useAudio';
import { mediaCS } from '@/configs/storages/client';
import type {
  ChatMediaGetTokenOfJoinParams,
  GetMediaTokenParams,
  MediaOperateOfJoinParams,
  MediaSyncInfoParams,
  MediaCallParams,
  ChatMediaInfo,
  GetMediaInfoParams,
  ChatMediaInfoPartial,
} from '../hooks/useChatMediaCall/types';
import { CallOperateCode, ChatMediaType } from '../hooks/useChatMediaCall/types';
import {
  ChatMediaGetTokenType,
  type MediaCallOperateParams,
  type ChatMediaGetTokenOfCallParams,
} from '../hooks/useChatMediaCall/types';
import {
  ChatMessagePayloadType,
  ChatMessageStatus,
} from '../hooks/useChatMessages/types';
import { serverTime } from '../hooks/useSystemTime/utils';
import type { ChatContextHooksFull, ChatContextHooksProps } from '../../context';
import { ChatCategory } from '../hooks/useChats/types';
import { ChatSocketControls } from '../hooks/useChatSocket/types';
import type { chat } from '../protobuf/protobufs/chat';
import { MediaType } from '../hooks/useQuickVO/types';

export type UseChatMediaValueResult = ReturnType<typeof useChatMediaValue>;

export function useChatMediaValue({ hooks }: ChatContextHooksProps) {
  const {
    session: { user },
  } = useSessionContext();

  const { play: playCallSound, stop: stopCallSound } = useAudio({
    src: '/media/call.mp3',
    loop: true,
  });

  const { chatSocket, chats, chatMessages, chatMediaCall, chatMediaQuickVO } = hooks;

  const { send } = chatSocket;

  const { joinRoom, publish, quitRoomEx, setMediaTrackConstraints } = chatMediaQuickVO;

  const [loading, setLoading] = useState(false);

  const timerRef = useRef<any>();

  const isPutThrough = useMemo(() => {
    return chatMediaCall.chatMediaInfo?.status === ChatMessageStatus.CALL_PUT_THROUGH;
  }, [chatMediaCall.chatMediaInfo?.status]);

  const isReady = useMemo(() => isPutThrough && !loading, [isPutThrough, loading]);

  const startTime = useMemo(() => {
    if (isReady) {
      return serverTime.time;
    }
    return undefined;
  }, [isReady]);

  const sendMediaToken = useCallback(
    async (params: chat.IGetMediaCallTokenRequest, retryOptions?: RetryOptions) => {
      const res = await send(
        {
          headers: {
            control: ChatSocketControls.GET_MEDIA_TOKEN,
          },
          body: params,
        },
        {
          ...retryOptions,
          isRetry(err) {
            return err.code !== 2;
          },
        }
      );
      return res.body;
    },
    [send]
  );

  const sendMediaOperateOfCall = useCallback(
    async (params: chat.IMediaCallOperateV2Request, retryOptions?: RetryOptions) => {
      const res = await send(
        {
          headers: {
            control: ChatSocketControls.MEDIA_OPERATE_CALL,
          },
          body: params,
        },
        retryOptions
      );
      return res.body;
    },
    [send]
  );

  const sendMediaOperateOfJoin = useCallback(
    async (
      params: chat.IMediaCallOperateCalleeV2Request,
      retryOptions?: RetryOptions
    ) => {
      const res = await send(
        {
          headers: {
            control: ChatSocketControls.MEDIA_OPERATE_JOIN,
          },
          body: params,
        },
        retryOptions
      );
      return res.body;
    },
    [send]
  );

  const sendGetMediaCallInfo = useCallback(
    async (params: chat.ISyncMediaCallInfoV2Request, retryOptions?: RetryOptions) => {
      const res = await send(
        {
          headers: {
            control: ChatSocketControls.GET_MEDIA_INFO,
          },
          body: params,
        },
        retryOptions
      );
      return res.body as chat.SyncMediaCallInfoV2Response;
    },
    [send]
  );

  const getMediaToken = useCallback(
    async ({ chat_id, related_gid, handlers: { sendBefore } }: GetMediaTokenParams) => {
      const chat = await chats.getChat({ chat_id });
      if (chat) {
        const isSingle = chat.category === ChatCategory.SINGLE;
        const { rtcToken, isBusy, roomId } = await sendMediaToken({
          chatId: related_gid || chat.chat_id,
          type: isSingle ? ChatMediaGetTokenType.SINGLE : ChatMediaGetTokenType.GROUP,
          ...(await sendBefore?.(chat)),
        });

        const mediaCallToken = {
          chat,
          rtc_token: rtcToken,
          is_busy: isBusy,
          room_id: roomId,
        } as ChatMediaInfo;

        chatMediaCall.setChatMediaInfo({ [chat.chat_id]: mediaCallToken });

        return mediaCallToken;
      }
    },
    [chats, sendMediaToken, chatMediaCall]
  );

  const getMediaTokenOfCall = useCallback(
    async ({ chat_id, related_gid }: ChatMediaGetTokenOfCallParams) => {
      return getMediaToken({
        chat_id,
        related_gid,
        handlers: {
          sendBefore(chat) {
            return {
              targetId:
                chat.category === ChatCategory.SINGLE
                  ? ''
                  : related_gid || chat.chat_id,
            };
          },
        },
      });
    },
    [getMediaToken]
  );

  const getMediaTokenOfJoin = useCallback(
    async ({ chat_id, room_id }: ChatMediaGetTokenOfJoinParams) => {
      return getMediaToken({
        chat_id,
        handlers: {
          sendBefore(chat) {
            setMediaTrackConstraints(
              MediaType.MICROPHONECAMERA_VIDEO,
              chat.category === ChatCategory.SINGLE
                ? { width: 1920, height: 1080 }
                : { width: 640, height: 480 }
            );
            return {
              targetId: chat.category === ChatCategory.SINGLE ? room_id : chat.chat_id,
            };
          },
        },
      });
    },
    [getMediaToken, setMediaTrackConstraints]
  );

  const getMediaCallingInfo = useCallback(
    (params: MediaSyncInfoParams) => {
      return sendGetMediaCallInfo({
        roomId: params.room_id,
      }).then((res) => {
        // TODO amo
        return res.createUser ? res : undefined;
      });
    },
    [sendGetMediaCallInfo]
  );

  const getMediaCallingInfoFull = useCallback(
    async (params: GetMediaInfoParams) => {
      const callingInfo = await getMediaCallingInfo({
        room_id: params.room_id,
      });

      if (callingInfo) {
        let caller: User;
        const callMembers: User[] = [];
        const callMembersIds = callingInfo.members.map((user) => user.userId!);

        await membersApis
          .getUsersByUserId(
            Array.from(new Set(callMembersIds.concat(callingInfo.createUser)))
          )
          .then((users) => {
            users.forEach((user, i) => {
              const user_ = {
                ...callingInfo.members[i],
                ...user,
                userId: user.user_id,
              };
              if (user_.user_id === callingInfo.createUser) {
                caller = user_;
              }
              if (callMembersIds.includes(user_.user_id)) {
                callMembers.push(user_);
              }
            });
          });

        return {
          type: callingInfo.busy as ChatMediaType,
          caller: caller!,
          callMembers: callMembers,
          payload_type:
            callingInfo.type === 1
              ? ChatMessagePayloadType.MEDIA_VIDEO_CALL
              : ChatMessagePayloadType.MEDIA_AUDIO_CALL,
        };
      }
    },
    [getMediaCallingInfo]
  );

  const syncMediaCallingInfo = useCallback(
    async (chatMediaInfo?: ChatMediaInfoPartial) => {
      const chatMediaInfo_ = chatMediaInfo || chatMediaCall.chatMediaInfo;
      if (chatMediaInfo_ && chatMediaInfo_.chat && chatMediaInfo_.room_id) {
        // TODO amo: Status
        const callingInfo = await getMediaCallingInfoFull({
          room_id: chatMediaInfo_.room_id,
        });
        if (callingInfo) {
          chatMediaCall.setChatMediaInfo({
            [chatMediaInfo_.chat.chat_id]: {
              ...chatMediaInfo,
              type: callingInfo.type,
              callMembers: callingInfo.callMembers,
              caller: callingInfo.caller,
              payload_type: callingInfo.payload_type,
            },
          });
        } else {
          chatMediaCall.clearChatMediaInfo(chatMediaInfo_.chat.chat_id);
        }
      }
    },
    [chatMediaCall, getMediaCallingInfoFull]
  );

  const mediaOperateOfCall = useCallback(
    async ({
      chat_id,
      payload_type,
      room_id,
      type = ChatMediaType.SINGLE,
      callMembers = [],
    }: MediaCallOperateParams) => {
      const { chatMediaValue } = hooks as ChatContextHooksFull;
      const chat = await chats.getChat({ chat_id });
      if (chat && user) {
        const cli_msg_id = genId();

        try {
          const res = await chatMediaValue.sendMediaOperateOfCall({
            chatId: chat_id,
            groupId: chat_id,
            cliMsgId: cli_msg_id,
            opeType: payload_type,
            roomId: room_id,
            targetIds: callMembers.map((item) => item.userId),
            originUid: user.userId,
            busy: type,
          });

          const resRecord = res.records[0];
          if (!resRecord || resRecord.code === CallOperateCode.RECEIVE_NO_FRIEND) {
            throw resRecord;
          }

          res.messageId = resRecord.messageId;

          if (res.messageId) {
            chatMediaCall.setChatMediaInfo({
              [chat_id]: {
                type,
                caller: user,
                chat,
                payload_type,
                owner_id: user.userId,
                callMembers: [{ ...user }, ...callMembers],
                cli_msg_id,
                message_id: res.messageId,
              },
            });
          }
        } catch (err: any) {
          if (err?.code !== CallOperateCode.RECEIVE_NO_FRIEND) {
            chatMediaCall.setChatMediaInfo({
              [chat_id]: {
                status: ChatMessageStatus.CALL_NETWORK_ERROR,
              },
            });
          }
          throw err;
        }
      }
    },
    [chats, hooks, user, chatMediaCall]
  );

  const mediaOperateOfJoin = useCallback(
    async ({ status, chatMediaInfo }: MediaOperateOfJoinParams) => {
      const chatMediaInfo_ =
        chatMediaInfo || (await chatMediaCall.getActivatedChatMediaInfo());
      if (chatMediaInfo_ && chatMediaInfo_.chat) {
        const { chatMediaValue } = hooks as ChatContextHooksFull;
        const { chat, message_id, room_id, caller } = chatMediaInfo_;
        const chat_id = chat.chat_id!;
        const user_id = user!.userId;
        try {
          const res = await chatMediaValue.sendMediaOperateOfJoin({
            opeType: status,
            roomId: room_id,
            originUid: caller!.userId,
            messageId: message_id,
          });

          chatMediaCall.setChatMediaInfo({
            [chat_id]: {
              status,
              owner_id: user_id,
            },
          });

          await chatMessages.updateMessages([
            {
              chat_id: chat_id,
              messages: [
                {
                  message_id: message_id,
                  status,
                  owner_id: user_id,
                  update_at: res.createdAt || serverTime.time,
                },
              ],
            },
          ]);

          // Update Chat
          await chats.insertChats([
            {
              chat_id: chat_id,
              update_at: res.createdAt || serverTime.time,
            },
          ]);
        } catch (err) {
          chatMediaCall.setChatMediaInfo({
            [chat_id]: {
              status: ChatMessageStatus.CALL_NETWORK_ERROR,
              owner_id: user_id,
            },
          });

          chatMessages.updateMessages([
            {
              chat_id,
              messages: [
                {
                  message_id,
                  status: ChatMessageStatus.CALL_NETWORK_ERROR,
                  owner_id: user_id,
                },
              ],
            },
          ]);
          throw err;
        }
      }
    },
    [chatMediaCall, hooks, user, chatMessages, chats]
  );

  const mediaError = useCallback(
    async (chatMediaInfo?: ChatMediaInfo) => {
      await mediaOperateOfJoin({
        status: ChatMessageStatus.CALL_NETWORK_ERROR,
        chatMediaInfo,
      });
    },
    [mediaOperateOfJoin]
  );

  const mediaCall = useCallback(
    async (
      params: MediaCallParams,
      handlers: {
        mediaOperateOfCall: typeof mediaOperateOfCall;
      }
    ) => {
      if (chatMediaCall.chatMediaInfo) {
        return;
      }

      const { payload_type, chat_id } = params;
      const { mediaOperateOfCall } = handlers;

      try {
        setLoading(true);
        await promiseTimeout(async () => {
          const tokenRes = await getMediaTokenOfCall(params);
          if (tokenRes) {
            await mediaOperateOfCall({
              ...params,
              room_id: tokenRes.room_id,
            });

            await joinRoom({
              userId: user!.userId,
              roomId: tokenRes.room_id,
              sdkToken: tokenRes.rtc_token,
              callType: 1,
              newPublishAutoSubscribe: false,
            });

            publish(
              payload_type === ChatMessagePayloadType.MEDIA_AUDIO_CALL
                ? MediaType.MICROPHONECAMERA_AUDIO
                : [MediaType.MICROPHONECAMERA_VIDEO, MediaType.MICROPHONECAMERA_AUDIO]
            )?.catch(console.error);
          }
        });
      } catch (err: any) {
        if (err?.code !== CallOperateCode.RECEIVE_NO_FRIEND) {
          await mediaError().catch(console.error);
          message.error('Network error.');
        }
        chatMediaCall.clearChatMediaInfo(chat_id);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [chatMediaCall, getMediaTokenOfCall, joinRoom, mediaError, publish, user]
  );

  const mediaJoin = useCallback(
    async (chatMediaInfo?: ChatMediaInfoPartial) => {
      const chatMediaInfo_ = chatMediaInfo || chatMediaCall.chatMediaInfo;
      if (chatMediaInfo_) {
        const { chat, room_id, payload_type } = chatMediaInfo_;

        if (chat) {
          try {
            setLoading(true);
            await promiseTimeout(async () => {
              const tokenRes = await getMediaTokenOfJoin({
                chat_id: chat.chat_id,
                room_id,
              });

              if (tokenRes) {
                await joinRoom({
                  userId: user!.userId,
                  roomId: tokenRes.room_id,
                  sdkToken: tokenRes.rtc_token,
                  callType: 1,
                  newPublishAutoSubscribe: false,
                });

                await mediaOperateOfJoin({
                  status: ChatMessageStatus.CALL_PUT_THROUGH,
                  chatMediaInfo: {
                    ...chatMediaInfo_,
                  } as ChatMediaInfo,
                });

                publish(
                  payload_type === ChatMessagePayloadType.MEDIA_AUDIO_CALL
                    ? MediaType.MICROPHONECAMERA_AUDIO
                    : [
                        MediaType.MICROPHONECAMERA_VIDEO,
                        MediaType.MICROPHONECAMERA_AUDIO,
                      ]
                )?.catch(console.error);
              }
            });
          } catch (err) {
            await mediaError().catch(console.error);
            chatMediaCall.clearChatMediaInfo(chat.chat_id);
            message.error('Network error.');
            throw err;
          } finally {
            setLoading(false);
          }
        }
      }
    },
    [
      chatMediaCall,
      getMediaTokenOfJoin,
      joinRoom,
      mediaError,
      mediaOperateOfJoin,
      publish,
      user,
    ]
  );

  const mediaDeclined = useCallback(async () => {
    await mediaOperateOfJoin({
      status: ChatMessageStatus.CALL_REJECTED,
    });
  }, [mediaOperateOfJoin]);

  const mediaHangUp = useCallback(async () => {
    const chatMediaInfo = chatMediaCall.chatMediaInfo;
    if (chatMediaInfo) {
      if (chatMediaInfo.status === ChatMessageStatus.CALL_PUT_THROUGH) {
        await mediaOperateOfJoin({
          status: ChatMessageStatus.CALL_HANG_UP,
        });
      } else {
        await mediaOperateOfJoin({
          status: ChatMessageStatus.CALL_CANCELLED,
        });
      }
    }
  }, [chatMediaCall.chatMediaInfo, mediaOperateOfJoin]);

  const mediaTimeout = useCallback(async () => {
    await mediaOperateOfJoin({
      status: ChatMessageStatus.CALL_TIMEOUT,
    });
  }, [mediaOperateOfJoin]);

  const mediaEndCall = useCallback(
    async (chatMediaInfo?: ChatMediaInfoPartial) => {
      const chatMediaInfo_ = chatMediaInfo || chatMediaCall.chatMediaInfo;
      if (chatMediaInfo_?.chat?.chat_id) {
        chatMessages.updateMessages([
          {
            chat_id: chatMediaInfo_.chat.chat_id,
            messages: [
              {
                message_id: chatMediaInfo_.message_id,
                cli_msg_id: chatMediaInfo_.cli_msg_id,
                read_at: serverTime.time,
              },
            ],
          },
        ]);
      }
    },
    [chatMediaCall.chatMediaInfo, chatMessages]
  );

  const clearCallData = useCallback(() => {
    stopCallSound();
    clearTimeout(timerRef.current);
    timerRef.current = undefined;
  }, [stopCallSound]);

  const mediaExit = useCallback(
    async (chatMediaInfo: ChatMediaInfoPartial) => {
      await quitRoomEx();
      mediaEndCall();
      if (chatMediaInfo.chat) {
        chatMediaCall.exitChatMedia();
        syncMediaCallingInfo({
          chat: chatMediaInfo.chat,
          room_id: chatMediaInfo.room_id,
          payload_type: chatMediaInfo.payload_type,
        });
      }
    },
    [chatMediaCall, mediaEndCall, quitRoomEx, syncMediaCallingInfo]
  );

  const refs = useRefs({
    isPutThrough,
    chatMediaCall,
    quitRoomEx,
    mediaError,
    sendMediaOperateOfJoin,
    getMediaCallingInfo,
    mediaTimeout,
    user,
    chatMessages,
    mediaHangUp,
    mediaEndCall,
    mediaOperateOfJoin,
    syncMediaCallingInfo,
    stopCallSound,
    playCallSound,
    clearCallData,
    mediaExit,
  });

  // Handle call status
  useLayoutEffect(() => {
    const {
      chatMediaCall,
      mediaExit,
      user,
      mediaTimeout,
      syncMediaCallingInfo,
      playCallSound,
      clearCallData,
    } = refs.current;
    const chatMediaInfo = chatMediaCall.chatMediaInfo;
    if (chatMediaInfo && chatMediaInfo.caller && user?.userId) {
      const isCaller = chatMediaInfo.caller.userId === user.userId;

      if (chatMediaCall.isMediaPayloadTypeCall(chatMediaInfo.payload_type)) {
        if (isCaller) {
          chatMediaCall.setOpenMediaModal(true);
          if (chatMediaInfo.chat && chatMediaInfo.type !== ChatMediaType.SINGLE) {
            chatMediaCall.setChatMediaInfo({
              [chatMediaInfo.chat?.chat_id]: {
                status: ChatMessageStatus.CALL_PUT_THROUGH,
              },
            });
          }
        } else {
          chatMediaCall.setOpenMediaNotice(true);
          if (chatMediaInfo.status) {
            chatMediaCall.setOpenMediaNotice(false);
            if (chatMediaInfo.status === ChatMessageStatus.CALL_PUT_THROUGH) {
              chatMediaCall.setOpenMediaModal(true);
            }
          }
        }

        if (chatMediaInfo.status) {
          clearCallData();
        } else if (timerRef.current === undefined) {
          timerRef.current = setTimeout(() => {
            mediaTimeout();
          }, 60 * 1000);
          playCallSound();
        }
      }

      if (chatMediaCall.isMediaPayloadTypeEnd(chatMediaInfo.status)) {
        mediaExit(chatMediaInfo);
      } else {
        syncMediaCallingInfo();
      }
    } else {
      clearCallData();
    }
  }, [
    refs,
    chatMediaCall.chatMediaInfo?.status,
    chatMediaCall.chatMediaInfo?.payload_type,
  ]);

  // Save call info
  useEffect(() => {
    if (chatMediaCall.chatMediaInfo) {
      const { payload_type, status } = chatMediaCall.chatMediaInfo;
      switch (status || payload_type) {
        case ChatMessagePayloadType.MEDIA_AUDIO_CALL:
        case ChatMessagePayloadType.MEDIA_VIDEO_CALL:
        case ChatMessageStatus.CALL_PUT_THROUGH: {
          const info: Record<string, any> = { ...chatMediaCall.chatMediaInfo };
          delete info.callMembers;
          delete info.rtc_token;
          mediaCS.setItem('info', info);
          break;
        }
        default:
          mediaCS.removeItem('info');
          break;
      }
    }
  }, [chatMediaCall.chatMediaInfo]);

  // Reconnect call
  useEffect(() => {
    if (chatSocket.socket) {
      (async () => {
        const info = await mediaCS.getItem('info');
        if (info?.room_id) {
          const { room_id, chat } = info;
          const {
            chatMediaCall,
            getMediaCallingInfo,
            mediaEndCall,
            mediaOperateOfJoin,
          } = refs.current;
          const stateInfo = await chatMediaCall.getChatMediaInfo(chat.chat_id);

          // Try active media
          if (stateInfo && stateInfo.room_id === room_id) {
            const res = await chatApis.activeCall({ room_id });
            if (res.code === ActiveCallResCode.SUCCESS) {
              return;
            }
          }

          // Clear media
          const callingInfo = await getMediaCallingInfo({
            room_id,
          });
          if (callingInfo) {
            await mediaOperateOfJoin({
              status: ChatMessageStatus.CALL_NETWORK_ERROR,
              chatMediaInfo: info,
            });
          }
          mediaEndCall();
          await mediaCS.removeItem('info');
        }
      })();
    }
  }, [chatSocket.socket, refs]);

  // Update call time
  useEffect(() => {
    const time = startTime ? startTime : isPutThrough ? serverTime.time : 0;

    if (time) {
      const {
        chatMediaCall: { chatMediaInfo },
        chatMessages,
      } = refs.current;
      if (chatMediaInfo) {
        const { chat, message_id, cli_msg_id } = chatMediaInfo;

        if (chat && (message_id || cli_msg_id)) {
          chatMessages.updateMessages([
            {
              chat_id: chat.chat_id!,
              messages: [{ message_id, cli_msg_id, create_at: time, update_at: time }],
            },
          ]);
        }
      }
    }
  }, [refs, isPutThrough, startTime]);

  return useMemo(
    () => ({
      sendMediaToken,
      sendMediaOperateOfCall,
      sendMediaOperateOfJoin,
      sendGetMediaCallInfo,
      mediaOperateOfCall,
      mediaOperateOfJoin,
      getMediaTokenOfCall,
      getMediaTokenOfJoin,
      getMediaCallingInfoFull,
      getMediaCallingInfo,
      syncMediaCallingInfo,
      mediaCall,
      mediaJoin,
      mediaDeclined,
      mediaHangUp,
      mediaTimeout,
      mediaError,
      mediaEndCall,
      mediaExit,
      mediaIsPutThrough: isPutThrough,
      mediaLoading: loading,
      mediaIsReady: isReady,
      mediaStartTime: startTime,
    }),
    [
      sendMediaToken,
      sendMediaOperateOfCall,
      sendMediaOperateOfJoin,
      sendGetMediaCallInfo,
      mediaOperateOfCall,
      mediaOperateOfJoin,
      getMediaTokenOfCall,
      getMediaTokenOfJoin,
      getMediaCallingInfoFull,
      getMediaCallingInfo,
      syncMediaCallingInfo,
      mediaCall,
      mediaJoin,
      mediaDeclined,
      mediaHangUp,
      mediaTimeout,
      mediaError,
      mediaEndCall,
      isPutThrough,
      loading,
      isReady,
      startTime,
      mediaExit,
    ]
  );
}
